import {
  Alert,
  EmptyState,
  FormField,
  PageHeader,
  Select,
  SelectItem,
  Skeleton,
  Tabs,
} from '@ntconsult/aluma-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useExportSummary } from '../hooks/useExportSummary';
import { useSummary } from '../hooks/useSummary';
import { useSummaryFilters } from '../hooks/useSummaryFilters';
import { SUMMARY_HORIZONS, type SummaryHorizon, type SummaryUnit } from '../types/Summary';
import { formatDisplayDate } from '../utils/data';
import { i18nKeyForError, isSessionError, parseApiError } from '../utils/errors';
import { ExportButton } from './ExportButton';
import { NoIdentityState } from './NoIdentityState';
import { ReferenceDateField } from './ReferenceDateField';
import { SummaryTable } from './SummaryTable';
import { PINNED_BLOCK, WIDE_PAGE } from './wideTable';

export function SummaryPage() {
  const { t, i18n } = useTranslation('alocacao');
  const filterState = useSummaryFilters();
  const query = useSummary(filterState.filters);
  const exportSummary = useExportSummary(filterState.filters);

  const [unit, setUnit] = useState<SummaryUnit>('BR');

  const { data, isLoading, isError } = query;
  const error = parseApiError(query.error);
  const isListLoading = isLoading || query.isPlaceholderData;

  if (isError && error && isSessionError(error)) return <NoIdentityState code={error.code} />;

  const noData = data && data.rows.length === 0;
  const unitsPresent = data
    ? (['BR', 'US'] as const).filter((u) => data.rows.some((row) => row.unitCode === u))
    : [];
  const activeUnit = unitsPresent.includes(unit) ? unit : (unitsPresent[0] ?? 'BR');

  return (
    <div className={`flex flex-col gap-6 ${WIDE_PAGE}`}>
      <div className={PINNED_BLOCK}>
        <PageHeader
          title={t('consultas.sintese.titulo')}
          description={t('consultas.sintese.descricao')}
        />
      </div>

      <section className="flex min-w-0 flex-col gap-4" aria-label={t('consultas.sintese.titulo')}>
        <div className={`flex flex-wrap items-start gap-4 ${PINNED_BLOCK}`}>
          <ReferenceDateField
            value={filterState.date}
            appliedDate={data?.date}
            onChange={filterState.setDate}
            hint={t('consultas.sintese.dataReferenciaDica')}
          />
          <FormField label={t('consultas.sintese.horizonte')} className="w-48">
            <Select
              value={filterState.horizon}
              onValueChange={(value) => filterState.setHorizon(value as SummaryHorizon)}
            >
              {SUMMARY_HORIZONS.map((horizon) => (
                <SelectItem key={horizon} value={horizon}>
                  {t('consultas.sintese.horizonteSemanas', { count: Number(horizon) })}
                </SelectItem>
              ))}
            </Select>
          </FormField>
          <div className="mt-[26px] flex h-10 items-center">
            <ExportButton
              exportMutation={exportSummary}
              disabled={!data || data.rows.length === 0}
              labels={{
                title: t('consultas.sintese.exportarTitulo'),
                description: t('consultas.sintese.exportarDescricao'),
              }}
            />
          </div>
        </div>

        {isListLoading && (
          <div className="flex flex-col gap-3">
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
            <Skeleton className="h-9 w-full" />
          </div>
        )}

        {isError && (
          <Alert
            variant="danger"
            title={t((error ? i18nKeyForError(error.code) : 'erros.unknown') as never)}
          />
        )}

        {!isListLoading && !isError && noData && (
          <EmptyState
            title={t('consultas.sintese.vazioTitulo', {
              date: formatDisplayDate(data.date, i18n.language),
            })}
            description={t('consultas.sintese.vazioDescricao')}
          />
        )}

        {!isListLoading &&
          !isError &&
          data &&
          !noData &&
          (unitsPresent.length === 1 ? (
            <SummaryTable data={data} unit={unitsPresent[0]} />
          ) : (
            <Tabs value={activeUnit} onValueChange={(value) => setUnit(value as SummaryUnit)}>
              <Tabs.List>
                {unitsPresent.map((u) => (
                  <Tabs.Trigger key={u} value={u}>
                    {u === 'BR' ? t('consultas.sintese.secaoBR') : t('consultas.sintese.secaoUS')}
                  </Tabs.Trigger>
                ))}
              </Tabs.List>
              {unitsPresent.map((u) => (
                <Tabs.Content key={u} value={u} className="mt-4">
                  <SummaryTable data={data} unit={u} />
                </Tabs.Content>
              ))}
            </Tabs>
          ))}
      </section>
    </div>
  );
}
